// Idempotent normalization of flow-diagram node ids to `<videoPartName>_<n>`.
//
// Scene nodes are renamed so their id correlates with the video part they
// reference (`node.label` holds the video part name); every persisted
// reference to a renamed id is rewritten with it: edge source/target,
// nodeLayout entries (including composite `url-node::<sceneId>::…` keys) and
// interactive mods that jump to a node (`value`/`jumpValue`/`functionString`,
// both as plain objects and inside `dataStr`-serialized mods).
//
// Pure and idempotent: inputs are never mutated, unchanged values are returned
// by reference, and a second run over its own output produces no changes.
// This exact file also exists in the platform-pls repo (src/models/project/) — keep the copies in sync.

const SCENE_NODE_TYPE = "scene";
const URL_NODE_ID_PREFIX = "url-node::";

interface FlowNodeShape {
  id: string;
  label?: string | null;
  type?: string | null;
}

interface FlowEdgeShape {
  source: string;
  target: string;
}

interface FlowDiagramShape {
  nodes?: readonly FlowNodeShape[] | null;
  edges?: readonly FlowEdgeShape[] | null;
}

interface NodeLayoutEntryShape {
  nodeId: string;
}

interface NodeLayoutShape {
  entries?: readonly NodeLayoutEntryShape[] | null;
}

export interface NormalizeFlowNodeIdsInput {
  flowDiagram?: FlowDiagramShape | null;
  videoParts?: readonly unknown[] | null;
  nodeLayout?: NodeLayoutShape | null;
}

export interface NormalizeFlowNodeIdsResult<
  T extends NormalizeFlowNodeIdsInput,
> {
  changed: boolean;
  /** old node id → new node id, only for ids that actually change */
  idMap: Record<string, string>;
  /** `input` with `flowDiagram`/`videoParts`/`nodeLayout` rewritten; === input when unchanged */
  output: T;
}

function emptyRecord<V>(): Record<string, V> {
  return Object.create(null) as Record<string, V>;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Video part name → id-safe slug: any run of ASCII punctuation/whitespace
 * becomes `_`; word chars and everything non-ASCII (accents, other scripts)
 * are kept. Keeping `-`, quotes and `\` out of ids matters — editor code
 * derives handle/edge-id strings from node ids and parses them back around
 * those characters.
 */
export function slugifyVideoPartName(name: string): string {
  return name.replace(/[^\w\u0080-\uFFFF]+/g, "_").replace(/^_+|_+$/g, "");
}

/**
 * Id for a newly created scene node: the same `<videoPartName>_<n>` format the
 * selection-time normalization produces, so new nodes are born conforming and
 * never need migrating. Returns "" when the name has no usable slug — the
 * caller falls back to its legacy id scheme.
 */
export function nextSceneNodeId(
  videoPartName: string,
  usedIds: Iterable<string>
): string {
  const slug = slugifyVideoPartName(videoPartName);
  if (!slug) return "";
  const used = new Set(usedIds);
  let n = 1;
  while (used.has(`${slug}_${n}`)) {
    n += 1;
  }
  return `${slug}_${n}`;
}

/**
 * Decide the rename for every scene node. A node already named
 * `<slug>_<digits>` for its own video part keeps its id, so re-running the
 * normalization is a no-op. New ids are never reused from any pre-existing id,
 * which keeps the map free of chained replacements (no idMap value is another
 * idMap key).
 */
export function buildNodeIdMap(
  nodes: readonly FlowNodeShape[]
): Record<string, string> {
  const originalIds = new Set(nodes.map((node) => node.id));
  const finalIds = new Set<string>();
  const toRename: Array<{ id: string; slug: string }> = [];

  for (const node of nodes) {
    const slug =
      node.type === SCENE_NODE_TYPE && typeof node.label === "string"
        ? slugifyVideoPartName(node.label)
        : "";
    const suffix = slug ? node.id.slice(slug.length + 1) : "";
    // Duplicate node ids (corrupt data) are deliberately NOT deduplicated:
    // renaming one occurrence would rewrite shared references by old id for
    // both nodes and oscillate between numbers on every run. Keeping both
    // stable does no further harm and preserves idempotency.
    const conforms =
      slug !== "" && node.id.startsWith(`${slug}_`) && /^\d+$/.test(suffix);
    if (slug === "" || conforms) {
      finalIds.add(node.id);
    } else {
      toRename.push({ id: node.id, slug });
    }
  }

  // Null-prototype records: video part names like "constructor" or "toString"
  // must not resolve to inherited Object.prototype members when used as keys.
  const idMap = emptyRecord<string>();
  const nextNumberBySlug = emptyRecord<number>();
  for (const { id, slug } of toRename) {
    // A duplicated legacy id yields one rename; both occurrences converge.
    if (idMap[id]) continue;
    let n = nextNumberBySlug[slug] ?? 1;
    while (finalIds.has(`${slug}_${n}`) || originalIds.has(`${slug}_${n}`)) {
      n += 1;
    }
    const newId = `${slug}_${n}`;
    nextNumberBySlug[slug] = n + 1;
    finalIds.add(newId);
    idMap[id] = newId;
  }
  return idMap;
}

/**
 * Rewrite node-id references anywhere in a JSON-ish value: `nodeId` object
 * properties, plus the same references inside JSON-encoded strings (mod
 * `dataStr`, interactiveConcat `value` entries) at any nesting depth, and
 * inside non-JSON strings that embed `JSON.stringify({nodeId})` (generated
 * `functionString` sources). Returns the input reference when nothing changed.
 */
export function rewriteNodeRefs(
  value: unknown,
  idMap: Record<string, string>,
  ancestors: WeakSet<object> = new WeakSet()
): unknown {
  if (typeof value === "string") {
    return rewriteStringNodeRefs(value, idMap);
  }
  if (Array.isArray(value)) {
    // Wire payloads are JSON and cannot be cyclic, but the walk must not blow
    // the stack if an in-memory caller ever hands it a cyclic graph: a value
    // already on the current path is returned as-is. Tracking the path (not
    // all visited nodes) keeps legitimate shared references rewritten.
    if (ancestors.has(value)) return value;
    ancestors.add(value);
    let changed = false;
    const next = value.map((item) => {
      const rewritten = rewriteNodeRefs(item, idMap, ancestors);
      if (rewritten !== item) changed = true;
      return rewritten;
    });
    ancestors.delete(value);
    return changed ? next : value;
  }
  if (value !== null && typeof value === "object") {
    if (ancestors.has(value)) return value;
    ancestors.add(value);
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      const rewritten =
        key === "nodeId" && typeof entry === "string" && idMap[entry]
          ? idMap[entry]
          : rewriteNodeRefs(entry, idMap, ancestors);
      if (rewritten !== entry) changed = true;
      // defineProperty, not assignment: a "__proto__" key in payload data must
      // become an own property instead of silently mutating the prototype.
      Object.defineProperty(next, key, {
        value: rewritten,
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
    ancestors.delete(value);
    return changed ? next : value;
  }
  return value;
}

function rewriteStringNodeRefs(
  value: string,
  idMap: Record<string, string>
): string {
  if (!value.includes("nodeId")) return value;
  try {
    const parsed: unknown = JSON.parse(value);
    const rewritten = rewriteNodeRefs(parsed, idMap);
    return rewritten === parsed ? value : JSON.stringify(rewritten);
  } catch {
    // Not JSON — a functionString (generated, or hand-edited in code mode).
    // Best-effort source rewrite: match a `nodeId` key in JSON, JS-literal or
    // string-escaped form — any spacing, any quote style — and swap only the
    // id value. Ids buried in variables or concatenation are not reachable.
    let next = value;
    for (const [oldId, newId] of Object.entries(idMap)) {
      const pattern = new RegExp(
        `(\\bnodeId(?:\\\\?["'])?\\s*:\\s*\\\\?["'])${escapeRegExp(oldId)}(?=\\\\?["'])`,
        "g"
      );
      next = next.replace(pattern, `$1${newId}`);
    }
    return next;
  }
}

/** Layout keys are node ids, or `url-node::<sceneNodeId>::interactive-open::<n>` composites. */
function rewriteLayoutNodeId(
  nodeId: string,
  idMap: Record<string, string>
): string {
  if (idMap[nodeId]) return idMap[nodeId];
  if (nodeId.startsWith(URL_NODE_ID_PREFIX)) {
    const parts = nodeId.split("::");
    if (parts.length > 1 && idMap[parts[1]]) {
      parts[1] = idMap[parts[1]];
      return parts.join("::");
    }
  }
  return nodeId;
}

export function normalizeFlowNodeIds<T extends NormalizeFlowNodeIdsInput>(
  input: T
): NormalizeFlowNodeIdsResult<T> {
  const idMap = buildNodeIdMap(input.flowDiagram?.nodes ?? []);
  if (Object.keys(idMap).length === 0) {
    return { changed: false, idMap, output: input };
  }

  // Deep pass first (catches node refs inside node data payloads), then the
  // structural id fields the walk does not know about.
  const walkedDiagram = rewriteNodeRefs(
    input.flowDiagram,
    idMap
  ) as FlowDiagramShape;
  const flowDiagram: FlowDiagramShape = {
    ...walkedDiagram,
    nodes: (walkedDiagram.nodes ?? []).map((node) =>
      idMap[node.id] ? { ...node, id: idMap[node.id] } : node
    ),
    edges: (walkedDiagram.edges ?? []).map((edge) =>
      idMap[edge.source] || idMap[edge.target]
        ? {
            ...edge,
            source: idMap[edge.source] ?? edge.source,
            target: idMap[edge.target] ?? edge.target,
          }
        : edge
    ),
  };

  const videoParts = input.videoParts
    ? rewriteNodeRefs(input.videoParts, idMap)
    : input.videoParts;

  let nodeLayout: NodeLayoutShape | null | undefined = input.nodeLayout;
  if (nodeLayout?.entries) {
    let layoutChanged = false;
    const entries = nodeLayout.entries.map((entry) => {
      const rewritten = rewriteLayoutNodeId(entry.nodeId, idMap);
      if (rewritten === entry.nodeId) return entry;
      layoutChanged = true;
      return { ...entry, nodeId: rewritten };
    });
    if (layoutChanged) {
      nodeLayout = { ...nodeLayout, entries };
    }
  }

  const output = {
    ...input,
    flowDiagram,
    videoParts,
    nodeLayout,
  } as T;

  return { changed: true, idMap, output };
}
